/**
 * ShiftSummaryBar.jsx
 * CCH Care — Phase 2 — Layer 2
 * Last Modified: 2026-04-22
 *
 * Collapsible panel showing shift metrics and handoff notes.
 * Overdue badge is tappable — fires onOverdueClick to filter resident list.
 *
 * Changes (2026-04-22):
 * - Added `isFloatView` prop. When true, an "Overseeing" pill renders at
 *   the start of the compact row to make the supervisory context
 *   visually distinct from a rostered CNA's view. The dashboard
 *   otherwise looks identical between the two modes, which is
 *   confusing — a Care Admin viewing an empty shift might mistake it
 *   for their own roster being empty.
 *   The pill uses a muted indigo (not primary blue, not warning/danger)
 *   to communicate "different context" without alarming the user.
 */
import { COLORS } from '../../../config/constants';

import { formatTime } from '../../../utils/formatters';
const FLOAT_COLOR = '#6366F1';       // indigo-500 — clearly distinct from primary blue
const FLOAT_BG    = 'rgba(99,102,241,0.10)';
const FLOAT_BORDER = 'rgba(99,102,241,0.25)';

export default function ShiftSummaryBar({ summary, handoffNotes, expanded, onToggle, onOverdueClick, isFloatView }) {
  if (!summary) return null;

  const percent = summary.adl_percent || 0;
  const progressColor = percent >= 70 ? COLORS.accent : percent >= 30 ? COLORS.warning : COLORS.danger;
  const hasOverdue = summary.overdue_count > 0;

  return (
    <div style={{ background: COLORS.surface, borderBottom: `1px solid ${COLORS.border}`, userSelect: 'none' }}>
      {/* Compact Row */}
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onToggle()}
        style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '12px 16px', cursor: 'pointer' }}
      >
        {/* Float view pill — indicates Care Admin/Supervisor oversight mode */}
        {isFloatView && (
          <div
            title="You are overseeing this shift rather than directly rostered on it."
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              padding: '4px 10px',
              borderRadius: 999,
              background: FLOAT_BG,
              border: `1px solid ${FLOAT_BORDER}`,
              fontSize: 11,
              fontWeight: 700,
              color: FLOAT_COLOR,
              letterSpacing: 0.3,
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            <span aria-hidden="true">👁️</span>
            Overseeing
          </div>
        )}

        <Metric
          value={summary.assigned_patients}
          label="Residents"
          sub={summary.new_admits > 0 ? `${summary.new_admits} new` : null}
        />

        <div style={{ width: 1, height: 32, background: COLORS.border }} />

        {/* Overdue — tappable when > 0 */}
        <div
          role={hasOverdue ? 'button' : undefined}
          tabIndex={hasOverdue ? 0 : undefined}
          onClick={hasOverdue ? (e) => { e.stopPropagation(); onOverdueClick?.(); } : undefined}
          onKeyDown={hasOverdue ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); onOverdueClick?.(); } } : undefined}
          style={{
            textAlign: 'center', minWidth: 60,
            cursor: hasOverdue ? 'pointer' : 'default',
            borderRadius: 8,
            padding: hasOverdue ? '4px 8px' : '4px 0',
            background: hasOverdue ? COLORS.dangerLight : 'transparent',
            border: hasOverdue ? `1px solid ${COLORS.dangerAlpha}` : 'none',
            transition: 'background 0.15s',
          }}
          title={hasOverdue ? 'Tap to filter overdue residents' : ''}
        >
          <div style={{ fontSize: 20, fontWeight: 800, color: hasOverdue ? COLORS.danger : COLORS.accent }}>
            <span style={{ marginRight: 2 }}>{hasOverdue ? '⚠' : '✓'}</span>
            {summary.overdue_count}
          </div>
          <div style={{ fontSize: 11, color: hasOverdue ? COLORS.danger : COLORS.textMuted, marginTop: 1 }}>
            Overdue
            {hasOverdue && (
              <span style={{ display: 'block', fontSize: 9, fontWeight: 600, color: COLORS.danger, opacity: 0.8 }}>
                tap to view
              </span>
            )}
          </div>
        </div>

        <div style={{ width: 1, height: 32, background: COLORS.border }} />

        {/* ADL Progress */}
        <div style={{ flex: 1, minWidth: 100 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 12, color: COLORS.textMuted }}>ADL Progress</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: progressColor }}>
              {summary.adl_total_done}/{summary.adl_total_expected} ({percent}%)
            </span>
          </div>
          <div style={{ height: 6, borderRadius: 3, background: COLORS.bg, overflow: 'hidden' }}>
            <div style={{ width: `${percent}%`, height: '100%', borderRadius: 3, background: progressColor, transition: 'width 0.5s ease' }} />
          </div>
        </div>

        <span style={{ fontSize: 14, color: COLORS.textMuted, transition: 'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
      </div>

      {expanded && handoffNotes.length > 0 && (
        <div style={{ padding: '0 16px 16px', borderTop: `1px solid ${COLORS.border}`, marginTop: -1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, padding: '12px 0 8px' }}>
            Handoff Notes ({handoffNotes.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 200, overflowY: 'auto' }}>
            {handoffNotes.map(note => (
              <div key={note.handoff_note_id} style={{
                padding: '10px 12px', borderRadius: 8,
                background: note.priority === 'urgent' ? COLORS.dangerLight : note.priority === 'important' ? COLORS.warningLight : COLORS.bg,
                borderLeft: `3px solid ${note.priority === 'urgent' ? COLORS.danger : note.priority === 'important' ? COLORS.warning : COLORS.primary}`,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.text }}>
                    {note.author_name}
                    <span style={{ fontWeight: 400, color: COLORS.textMuted }}> · {note.from_shift_name}</span>
                  </span>
                  <span style={{ fontSize: 11, color: COLORS.textMuted }}>{formatTime(note.created_at)}</span>
                </div>
                {note.patient_label && (
                  <div style={{ fontSize: 12, color: COLORS.primary, fontWeight: 600, marginBottom: 4 }}>{note.patient_label}</div>
                )}
                <div style={{ fontSize: 13, color: COLORS.text, lineHeight: 1.4 }}>{note.note_text}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {expanded && handoffNotes.length === 0 && (
        <div style={{ padding: '8px 16px 16px', fontSize: 13, color: COLORS.textMuted, fontStyle: 'italic' }}>
          No handoff notes from the previous shift.
        </div>
      )}
    </div>
  );
}

function Metric({ value, label, sub, color, icon }) {
  return (
    <div style={{ textAlign: 'center', minWidth: 60 }}>
      <div style={{ fontSize: 20, fontWeight: 800, color: color || COLORS.text }}>
        {icon && <span style={{ marginRight: 2 }}>{icon}</span>}
        {value}
      </div>
      <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 1 }}>
        {label}
        {sub && <span style={{ display: 'block', fontSize: 10, color: COLORS.primary, fontWeight: 600 }}>{sub}</span>}
      </div>
    </div>
  );
}

